import React, { useRef } from 'react'
import { Alert, Checkbox, ErrorText, Input, TextTooltip, ToggleContainer } from '@root/components'
import { Controller, get, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IInvoice } from '@root/query'
import styled from 'styled-components'
import { FaQuestionCircle } from 'react-icons/fa'
import { useBusinessContext, useFeature } from '@root/context'
import { isEligibleForPostDueAutomation } from '@root/utils'
import { isGermanBusinessCountry } from '../xrechnungDelivery'

interface Props {
  toggleAutoOpen?: boolean
  isCreditNote: boolean
  hideBuyerReference?: boolean
}

export const InvoiceFormMoreSection: React.FC<Props> = ({
  toggleAutoOpen = false,
  isCreditNote,
  hideBuyerReference = false
}) => {
  const { data: business } = useBusinessContext()
  const containerRef = useRef(null)
  const {
    register,
    control,
    formState: { errors }
  } = useFormContext<Partial<IInvoice>>()

  const formData = useWatch({ control }) as Partial<IInvoice>

  const cannotUsePostDueAutomation = !isEligibleForPostDueAutomation(formData)

  const [t] = useTranslation()
  const { enabled: kraviaEnabled } = useFeature('kravia')
  const isGermanBusiness = isGermanBusinessCountry(business?.country)
  const showSoftReminderCheckbox = (business?.soft_reminder_steps?.length ?? 0) > 0
  const showKraviaCheckbox =
    kraviaEnabled && !!business?.kravia_enabled && !!business?.is_automatic_debt_collection_enabled
  const showPostDueAutomations = (showSoftReminderCheckbox || showKraviaCheckbox) && !isCreditNote

  return (
    <ToggleContainer
      title={t('invoicing.form.more')}
      openByDefault={toggleAutoOpen}
      ref={containerRef}
    >
      {/* OTHER */}
      <Input
        label={t('invoicing.form.reference')}
        errors={errors?.reference}
        info={t('invoicing.form.referenceInfo')}
        {...register('reference')}
      />

      {!hideBuyerReference && (
        <Input
          label={
            isGermanBusiness
              ? t('invoicing.form.buyerReferenceBt10')
              : t('invoicing.form.buyerReference')
          }
          errors={errors?.buyer_reference}
          info={
            isGermanBusiness
              ? t('invoicing.form.buyerReferenceInfoBt10')
              : t('invoicing.form.buyerReferenceInfo')
          }
          {...register('buyer_reference')}
        />
      )}

      <Input
        label={t('invoicing.form.sellerReference')}
        errors={errors?.seller_reference}
        info={t('invoicing.form.sellerReferenceInfo')}
        {...register('seller_reference')}
      />

      <Input
        label={t('invoicing.form.penaltyInterest')}
        type="number"
        info={t('invoicing.form.penaltyInterestInfo')}
        errors={errors?.penalty_interest}
        step="0.1"
        {...register('penalty_interest', { valueAsNumber: true })}
      />

      {showPostDueAutomations && (
        <StyledCheckboxWrapper>
          <Alert
            type="warning"
            isVisible={cannotUsePostDueAutomation}
            marginBottom="0.5rem"
            description={t(
              showKraviaCheckbox
                ? 'invoicing.form.automaticDebtCollectionAlert'
                : 'invoicing.form.softReminderAlert'
            )}
          />
          {showSoftReminderCheckbox && (
            <PostDueAutomationCheckbox
              name="is_automatic_soft_reminder_enabled"
              label={t('invoicing.form.softReminder')}
              info={t('invoicing.form.softReminderInfo')}
            />
          )}

          {showKraviaCheckbox && (
            <PostDueAutomationCheckbox
              name="is_automatic_debt_collection_enabled"
              label={t('invoicing.form.automaticDebtCollection')}
              info={t('invoicing.form.automaticDebtCollectionInfo')}
            />
          )}
        </StyledCheckboxWrapper>
      )}
    </ToggleContainer>
  )
}

const PostDueAutomationCheckbox: React.FC<{
  name: 'is_automatic_soft_reminder_enabled' | 'is_automatic_debt_collection_enabled'
  label: string
  info: string
}> = ({ name, label, info }) => {
  const { control, formState } = useFormContext<Partial<IInvoice>>()
  const error = get(formState.errors, name)

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <>
          <Checkbox
            {...field}
            checked={field.value}
            label={
              <StyledCheckboxLabelContainer>
                {label}
                <TextTooltip tooltip={info}>
                  <FaQuestionCircle />
                </TextTooltip>
              </StyledCheckboxLabelContainer>
            }
          />
          <div>{error && <ErrorText>{error.message}</ErrorText>}</div>
        </>
      )}
    />
  )
}

const StyledCheckboxLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const StyledCheckboxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-top: ${({ theme }) => theme.spacing.lg}rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg}rem;
`
